import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReportRoundedIcon from "@mui/icons-material/ReportRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import API_BASE_URL from "../config/appConfig";
import "./deliveryTracker.scss";

/* status → tone (reserved status colours), icon and the words for it. A
   status colour never carries meaning alone: the label always shows. */
export const VERDICTS = {
  on_track: { tone: "good", icon: CheckCircleRoundedIcon, label: "On track" },
  done: { tone: "good", icon: CheckCircleRoundedIcon, label: "All done" },
  at_risk: { tone: "warning", icon: WarningAmberRoundedIcon, label: "At risk" },
  off_track: { tone: "serious", icon: ErrorOutlineRoundedIcon, label: "Off track" },
  overdue: { tone: "critical", icon: ReportRoundedIcon, label: "Overdue" },
  early: { tone: "neutral", icon: HourglassEmptyRoundedIcon, label: "Too early to tell" },
  no_work: { tone: "neutral", icon: HourglassEmptyRoundedIcon, label: "No issues yet" },
  no_target: { tone: "neutral", icon: HourglassEmptyRoundedIcon, label: "No target date" },
};

const fmtDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "—");
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The one sentence under the verdict: why the board thinks so. */
export function explainVerdict(p, isKanban) {
  if (!p) return "";
  const target = isKanban ? "the release date" : "the sprint end";
  const left = Math.ceil(p.daysRemaining || 0);
  switch (p.status) {
    case "done": return `Every issue is done${isKanban ? " — ready to release" : ""}.`;
    case "no_work": return "Add issues to start tracking progress.";
    case "no_target": return `Set ${target} to get a forecast.`;
    case "early": return `Less than a day in — the forecast firms up once work starts landing. ${plural(p.pending, "issue")} to go.`;
    case "overdue": return `${fmtDate(p.targetDate)} has passed with ${plural(p.pending, "issue")} still open.`;
    case "on_track":
      return `At ${p.pace.actualPerDay}/day you'd finish ${p.projectedFinish ? fmtDate(p.projectedFinish) : "in time"} — before ${target} (${plural(left, "day")} left).`;
    case "at_risk":
    case "off_track":
      return p.projectedFinish
        ? `At ${p.pace.actualPerDay}/day you'd finish ${fmtDate(p.projectedFinish)}, after ${target}. You need ${p.pace.requiredPerDay ?? "—"}/day to make it.`
        : `Nothing is done yet. You need ${p.pace.requiredPerDay ?? "—"}/day for ${plural(left, "day")} to make ${target}.`;
    default: return "";
  }
}

/* ------------------------------------------------------------------ burn-up */
const W = 640;
const H = 220;
const PAD = { top: 16, right: 92, bottom: 28, left: 34 };

function BurnupChart({ series, total }) {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);
  if (!series?.length) return null;

  const max = Math.max(1, total, ...series.map((p) => p.done || 0));
  const x = (i) => PAD.left + (i / Math.max(1, series.length - 1)) * (W - PAD.left - PAD.right);
  const y = (v) => PAD.top + (1 - v / max) * (H - PAD.top - PAD.bottom);
  const line = (pick) =>
    series
      .map((p, i) => [i, pick(p)])
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([i, v], n) => `${n ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .join(" ");

  const lastDone = series.reduce((last, p, i) => (p.done !== null ? i : last), -1);
  const ticks = [0, Math.round(max / 2), max];
  const labelEvery = Math.max(1, Math.ceil(series.length / 6));

  const onMove = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((px - PAD.left) / (W - PAD.left - PAD.right)) * (series.length - 1));
    setHover(Math.max(0, Math.min(series.length - 1, i)));
  };

  const h = hover !== null ? series[hover] : null;

  return (
    <div className="dt-chart">
      <div className="dt-legend" aria-hidden="true">
        <span><i className="dt-swatch is-done" />Done (cumulative)</span>
        <span><i className="dt-swatch is-ideal" />On-time pace</span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Burn-up: ${series[lastDone]?.done ?? 0} of ${total} done; on-time pace reaches ${total} on ${series[series.length - 1].date}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className="dt-grid" x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
            <text className="dt-axis" x={PAD.left - 8} y={y(t) + 4} textAnchor="end">{t}</text>
          </g>
        ))}
        {series.map((p, i) => (i % labelEvery === 0 || i === series.length - 1 ? (
          <text key={p.date} className="dt-axis" x={x(i)} y={H - 8} textAnchor="middle">{moment(p.date).format("DD MMM")}</text>
        ) : null))}

        <path className="dt-line is-ideal" d={line((p) => p.ideal)} />
        <path className="dt-line is-done" d={line((p) => p.done)} />

        {/* direct labels at the line ends */}
        <text className="dt-end-label" x={x(series.length - 1) + 8} y={y(series[series.length - 1].ideal) + 4}>
          Target {total}
        </text>
        {lastDone >= 0 ? (
          <>
            <circle className="dt-dot" cx={x(lastDone)} cy={y(series[lastDone].done)} r={4.5} />
            <text className="dt-end-label is-strong" x={x(lastDone) + 8} y={y(series[lastDone].done) - 8}>
              {series[lastDone].done} done
            </text>
          </>
        ) : null}

        {h ? (
          <g className="dt-crosshair" pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} />
            {h.done !== null ? <circle className="dt-dot" cx={x(hover)} cy={y(h.done)} r={4.5} /> : null}
          </g>
        ) : null}
        {/* hit area larger than the marks */}
        <rect x={PAD.left} y={0} width={W - PAD.left - PAD.right} height={H} fill="transparent" />
      </svg>
      {h ? (
        <div
          className="dt-tooltip"
          style={{ left: `${(x(hover) / W) * 100}%` }}
          role="status"
        >
          <strong>{moment(h.date).format("ddd DD MMM")}</strong>
          <span>Done: {h.done === null ? "—" : h.done}</span>
          <span>On-time pace: {h.ideal}</span>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- the tracker */
const DeliveryTracker = ({ sprintId, isKanban = false, refreshKey = "" }) => {
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async () => {
    if (!sprintId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/sprints/progress/${sprintId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      });
      setProgress(data);
      setFailed(false);
    } catch (error) {
      console.log(error, "delivery progress");
      setFailed(true);
    }
  }, [sprintId]);

  // refreshKey changes whenever an issue moves, so the verdict follows the board
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const columnMax = useMemo(
    () => Math.max(1, ...(progress?.columns || []).map((c) => Math.max(c.count, c.wipLimit || 0))),
    [progress?.columns]
  );

  if (failed && !progress) {
    return (
      <div className="dt-strip is-quiet" role="status">
        Progress tracking is unavailable right now.
        <button type="button" className="dt-link" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!progress) return <div className="dt-strip is-loading" aria-busy="true" />;

  const verdict = VERDICTS[progress.status] || VERDICTS.no_work;
  const VerdictIcon = verdict.icon;
  const left = Math.ceil(progress.daysRemaining || 0);
  const targetWord = isKanban ? "Release" : "Sprint ends";

  return (
    <section className={`dt ${open ? "is-open" : ""}`} aria-label="Delivery tracking">
      <div className="dt-strip">
        <span className={`dt-verdict tone-${verdict.tone}`}>
          <VerdictIcon />
          {verdict.label}
        </span>

        <div className="dt-stats">
          <span className="dt-stat"><strong>{progress.done}</strong> done</span>
          <span className="dt-stat"><strong>{progress.pending}</strong> pending</span>
          <span className="dt-stat dt-stat--bar" title={`${progress.percent}% done`}>
            <span className="dt-bar"><span style={{ width: `${progress.percent}%` }} /></span>
            <strong>{progress.percent}%</strong>
          </span>
          {progress.targetDate ? (
            <span className="dt-stat">
              {targetWord} <strong>{fmtDate(progress.targetDate)}</strong>
              {progress.status !== "done" ? <em> · {left > 0 ? `${plural(left, "day")} left` : "today"}</em> : null}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className="dt-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="dt-report"
        >
          <InsightsRoundedIcon />
          {open ? "Hide report" : "View report"}
          <ExpandMoreRoundedIcon className="dt-toggle__chev" />
        </button>
      </div>

      <p className="dt-explain">{explainVerdict(progress, isKanban)}</p>

      {open ? (
        <div className="dt-report" id="dt-report">
          <div className="dt-tiles">
            <div className="dt-tile"><span>Total issues</span><strong>{progress.total}</strong></div>
            <div className="dt-tile"><span>Story points done</span><strong>{progress.points.done}<small>/{progress.points.total}</small></strong></div>
            <div className="dt-tile"><span>{isKanban ? "Throughput" : "Pace so far"}</span><strong>{progress.pace?.actualPerDay ?? "—"}<small>/day</small></strong></div>
            <div className="dt-tile"><span>Needed to finish</span><strong>{progress.pace?.requiredPerDay ?? "—"}<small>/day</small></strong></div>
            <div className="dt-tile"><span>Projected finish</span><strong className="is-date">{fmtDate(progress.projectedFinish)}</strong></div>
          </div>

          <div className="dt-panels">
            <div className="dt-panel dt-panel--wide">
              <div className="dt-panel__head">
                <h4>Burn-up to {isKanban ? "release" : "sprint end"}</h4>
                <button type="button" className="dt-link" onClick={() => setShowTable((v) => !v)}>
                  {showTable ? "Show chart" : "Show table"}
                </button>
              </div>
              {showTable ? (
                <div className="dt-table-wrap">
                  <table className="dt-table">
                    <thead><tr><th>Date</th><th>Done</th><th>On-time pace</th></tr></thead>
                    <tbody>
                      {progress.burnup.map((p) => (
                        <tr key={p.date}><td>{moment(p.date).format("DD MMM")}</td><td>{p.done ?? "—"}</td><td>{p.ideal}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <BurnupChart series={progress.burnup} total={progress.total} />
              )}
            </div>

            <div className="dt-panel">
              <div className="dt-panel__head"><h4>{isKanban ? "Columns & WIP" : "By status"}</h4></div>
              <ul className="dt-columns">
                {progress.columns.map((c) => (
                  <li key={c.statusId} className={c.overLimit ? "is-over" : ""}>
                    <span className="dt-columns__name">{c.title}</span>
                    <span className="dt-columns__bar" title={`${c.count}${c.wipLimit ? ` of ${c.wipLimit} allowed` : ""}`}>
                      <span style={{ width: `${(c.count / columnMax) * 100}%` }} />
                      {c.wipLimit ? <i style={{ left: `${(c.wipLimit / columnMax) * 100}%` }} /> : null}
                    </span>
                    <span className="dt-columns__value">
                      {c.count}{c.wipLimit ? `/${c.wipLimit}` : ""}
                      {c.overLimit ? <WarningAmberRoundedIcon aria-label="Over WIP limit" /> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="dt-panel">
              <div className="dt-panel__head"><h4>Who holds the work</h4></div>
              <table className="dt-table">
                <thead><tr><th>Person</th><th>Pending</th><th>Done</th></tr></thead>
                <tbody>
                  {progress.byAssignee.length ? progress.byAssignee.map((p) => (
                    <tr key={p.name}><td>{p.name}</td><td>{p.pending}</td><td>{p.done}</td></tr>
                  )) : <tr><td colSpan={3}>No issues yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default DeliveryTracker;
